import { Dispatch, useCallback, useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// eslint-disable-next-line import/no-extraneous-dependencies
import _ from 'lodash';

import { Button } from 'UIPackage/MainModule/components/Button';
import { knmTryOpenSignModal } from 'KnmPackage/MainModule/actions/knmActions';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IKnmErknmFormAct } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormAct';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IDecisionActForm } from 'DecisionPackage/MainModule/interfaces/IDecisionActForm';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { IKnmPmFormAct } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormAct';
import { decisionTryOpenSignModal } from 'DecisionPackage/MainModule/actions/decisionActions';
import { setDirtyActGuid } from 'CorePackage/HeaderModule/actions/headerActions';
import { IDecisionFormReason } from 'DecisionPackage/MainModule/interfaces/IDecisionFormReason';
import { fetchExcludedViolationsFromReinspection } from 'APIPackage/DecisionModule/services/decisionApi';
import { convertKnmDataRequirementsNpaToFormValuesDecision } from 'KnmErknmPackage/MainModule/common/convertKnmDataRequirementsNpaToFormValuesDecision';
import { IKnmErknmFormViolatedRequirementsNpa } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { NotificationRepeatViolationModal } from 'KnmPackage/KnmHeaderModule/components/NotifcationRepeatViolationModal/NotificationRepeatViolationModal';
import { convertIKnmErknmFormViolatedRequirementsNpaToIKnmErknmFormRequirementNpa } from 'KnmErknmPackage/MainModule/common/convertIKnmErknmFormViolatedRequirementsNpaToIKnmErknmFormRequirementNpa';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';
import {
  getKnmErknmIsPrescriptionFailed,
  getKnmErknmIsPrescriptionOldRules,
} from 'KnmErknmPackage/MainModule/selectors/knmErknmSelectors';

import styles from './KnmActSignButton.module.scss';
import { getIsDirtyAct } from './getIsDirtyAct';
import {
  getDecisionIsPrescriptionFailed,
  getDecisionIsPrescriptionOldRules,
} from '../../../DecisionPackage/MainModule/selectors/decisionSelectors';

interface IErknmActSignButtonProps {
  value: IKnmErknmFormAct | IDecisionActForm | IKnmPmFormAct;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  onSaveButtonClick: (() => void) | undefined;
  name: string;
  isDecision?: boolean;
  isPm?: boolean;
  actIndex?: number;
  isStatusFillInProcess?: boolean;
  isEnablePrescriptionCheck?: boolean;
  decisionId?: number;
}

export const KnmActSignButton = ({
  onSaveButtonClick,
  value,
  formRef,
  isDecision,
  isPm,
  actIndex,
  isStatusFillInProcess,
  isEnablePrescriptionCheck,
  decisionId,
  name,
}: IErknmActSignButtonProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const { dirtyFields, modified } = formRef?.current?.getState() || {};
  const { wasEditFiled: isDirtyAct, wasEditOnlyViolations: isDirtyActOnlyViolations } = getIsDirtyAct(
    dirtyFields,
    actIndex,
    isPm,
    isDecision,
    formRef as IKnmFormRef,
    modified,
  );
  const [isRequirementsNpaModalVisible, setIsRequirementsNpaModalVisible] = useState(false);
  const [willBeRemoveRequirements, setWillBeRemoveRequirements] = useState<IKnmErknmFormViolatedRequirementsNpa[]>([]);

  // глубокое сравнение полей акта на предмет изменения (да, костыль, но по другому никак в текущей реализации)
  const hasDirtyActFields = useMemo(() => {
    if (!dirtyFields || typeof dirtyFields !== 'object') {
      return false;
    }
    const actKeys = Object.keys(dirtyFields).filter(key => key.includes('acts'));

    if (actKeys.length === 0) {
      return false;
    }

    type FormWithGetFieldState = {
      getFieldState: (field: string) => { initial?: unknown; value?: unknown } | undefined;
    };
    const form = formRef?.current as FormWithGetFieldState | null | undefined;

    return actKeys.some(key => {
      const state = form?.getFieldState(key);
      const initial = state?.initial;
      const fieldValue = state?.value;

      return !_.isEqual(initial, fieldValue);
    });
  }, [formRef?.current?.getState()?.dirtyFields]);

  // @ts-ignore
  const reasons = formRef?.current.getFieldState('reasons')?.value as IDecisionFormReason[] | undefined;

  const requirementsCurrent = useMemo(() => {
    return convertIKnmErknmFormViolatedRequirementsNpaToIKnmErknmFormRequirementNpa(
      value.violatedMandatoryRequirements,
    );
  }, [value]);

  const isKnmPrescriptionOldRules = useSelector(getKnmErknmIsPrescriptionOldRules);
  const isKnmPrescriptionFailed = useSelector(getKnmErknmIsPrescriptionFailed);
  const isDecisionPrescriptionOldRules = useSelector(getDecisionIsPrescriptionOldRules);
  const isDecisionPrescriptionFailed = useSelector(getDecisionIsPrescriptionFailed);

  const isPrescriptionCheckByOldRules = isDecision ? isDecisionPrescriptionOldRules : isKnmPrescriptionOldRules;
  const isPassportByCheckPrescriptionFailed = isDecision ? isDecisionPrescriptionFailed : isKnmPrescriptionFailed;

  /**
   * Валидация неактивных нарушений при повторной проверке
   * если требование выведено в прод и у паспорта основание 'Проверка предписания'
   */
  const validateNotActiveViolations = useCallback(() => {
    const isPrescriptionCheck =
      reasons?.[0].reason?.type?.name === KnmReasonTypeNameEnum.PRESCRIPTION_CHECK &&
      isPrescriptionCheckByOldRules === false;

    if (
      isEnablePrescriptionCheck &&
      (isPrescriptionCheck || isPassportByCheckPrescriptionFailed === true) &&
      decisionId
    ) {
      return fetchExcludedViolationsFromReinspection(decisionId).then(requirements => {
        if (requirements?.length) {
          // Показываем модальное окно с предупреждением
          setIsRequirementsNpaModalVisible(true);
          setWillBeRemoveRequirements(convertKnmDataRequirementsNpaToFormValuesDecision(requirements) || []);

          return false;
        }

        return true;
      });
    }

    return new Promise<boolean>(resolve => resolve(true));
  }, [
    formRef,
    isEnablePrescriptionCheck,
    decisionId,
    isPrescriptionCheckByOldRules,
    isPassportByCheckPrescriptionFailed,
  ]);

  const handleOpenSignModalOrTestSignDecision = useCallback(actGuid => {
    dispatch(decisionTryOpenSignModal(actGuid));
  }, []);

  const onClickButtonSign = useCallback(async () => {
    if (value.guid) {
      if (!isDecision) {
        dispatch(knmTryOpenSignModal(value.guid));
      } else {
        if (isStatusFillInProcess) {
          const isValid = await validateNotActiveViolations();

          if (!isValid) return null;
        }

        handleOpenSignModalOrTestSignDecision(value.guid);
      }
    }
  }, [value, isDecision, isStatusFillInProcess]);

  useEffect(() => {
    if (!value.isNew && value.signed && value.guid) {
      const shouldUnsigned = isDirtyAct && !isDirtyActOnlyViolations;

      if (shouldUnsigned) {
        // @ts-ignore
        formRef?.current?.change(`${name}.signed`, false);
        dispatch(setDirtyActGuid(value.guid));
      }
    }
  }, [name, isDirtyAct, isDirtyActOnlyViolations]);

  const onClickButtonSave = useCallback(() => {
    onSaveButtonClick && onSaveButtonClick();
  }, [onSaveButtonClick]);

  let buttonTitle = '';
  let isButtonDisabled = false;
  let callback = () => {};

  if (value.signed) {
    if (isDirtyAct) {
      buttonTitle = 'Сохранить акт';
      callback = onClickButtonSave;
    } else {
      buttonTitle = 'Акт подписан';
      isButtonDisabled = true;
    }
  } else if (value.isNew || hasDirtyActFields || isDirtyAct) {
    buttonTitle = 'Сохранить акт';
    callback = onClickButtonSave;
  } else {
    buttonTitle = isDecision ? 'Подписать акт и паспорт' : 'Подписать акт';
    callback = onClickButtonSign;
  }

  return (
    <div className={styles.RowForButtonSign}>
      <Button
        color="primary"
        text={buttonTitle}
        id="saveButton"
        type={'button'}
        disabled={isButtonDisabled}
        onClick={callback}
        className={styles.ButtonSign}
      />
      <NotificationRepeatViolationModal
        requirementsCurrent={requirementsCurrent}
        requirementsNpa={willBeRemoveRequirements}
        visible={isRequirementsNpaModalVisible}
        onClose={() => setIsRequirementsNpaModalVisible(false)}
        onSubmit={() => handleOpenSignModalOrTestSignDecision(value.guid)}
      />
    </div>
  );
};
